import React, { useEffect, useState, useMemo } from 'react';
import {
  Form, Field, ErrorMessage, Formik,
} from 'formik';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import PropTypes from 'prop-types';
import {
  Grid, Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { newMomentDate, parseDate } from '~/utils/moment';
import {
  Card, CardBody, CardHeader, CardIcon,
} from '~/components/Card';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MSearchButton from '~/components/PF2MSearchButton';
import PF2MClearFiltersButton from '~/components/PF2MClearFiltersButton';
import PF2MDatepicker from '~/components/PF2MDatepicker';
import { useTranslation } from 'react-i18next';
import styles from './styles';
import {
  getElements,
  getOriginSubElements,
  getEquipmentTypes,
  getEquipmentsGroups,
  getEquipments,
  getShiftScales,
  getExceptionTypes,
  getTurnsGroups,
  getTurns,
  getTeamGroups,
  getTeams,
  getSubElements,
  getProjectGroupsElementPointsLinks,
} from '~/store/manager/actions';
import { setUndergroundFilters } from '~/store/dailyparts/actions';
import {
  getProjectGroups,
  getUnitOperationGroups,
  getUnitOperations,
  getCodeTypes,
  getCodeGroups,
  getCodes,
  getPriorities,
} from '~/store/underground/actions';

const SearchForm = ({ classes, onSearch, onClose }) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const dispatch = useDispatch();
  const filters = useSelector(state => state.dailyParts.filters);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const projectGroups = useSelector(state => state.underground.projectGroups);
  const projectGroupsElementPointsLinks = useSelector(
    state => state.manager.projectGroupsElementPointsLinks,
  );
  const { t: translate } = useTranslation();

  useEffect(() => {
    async function fetchData() {
      dispatch(getProjectGroups(true));
      await dispatch(getElements(true));
      dispatch(getUnitOperationGroups(true));
      dispatch(getUnitOperations(true));
      dispatch(getCodeTypes(true));
      dispatch(getCodeGroups(true));
      dispatch(getCodes(true));
      dispatch(getEquipmentTypes(true));
      dispatch(getEquipmentsGroups(true));
      dispatch(getEquipments(true));
      dispatch(getShiftScales(true));
      dispatch(getPriorities());
      dispatch(getExceptionTypes());
      dispatch(getTurnsGroups());
      dispatch(getTurns());
      dispatch(getTeamGroups());
      dispatch(getTeams());
      dispatch(getSubElements(true));
      dispatch(getProjectGroupsElementPointsLinks());
    }
    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, dispatch]);

  const subElementsById = useMemo(() => subElements.reduce((acc, e) => {
    acc[e.id] = e;
    return acc;
  }, {}), [subElements]);

  const groupToElementsMapper = useMemo(() => projectGroupsElementPointsLinks
    .reduce((acc, link) => {
      const groupId = link.id_project_group;
      const subElementId = link.id_element_point;

      const subElement = subElementsById[subElementId];
      if (!subElement) return acc;

      const elementId = subElement.id_element;

      if (!acc[groupId]) acc[groupId] = new Set();
      acc[groupId].add(elementId);

      return acc;
    }, {}), [projectGroupsElementPointsLinks, subElements]);

  const groupToSubElementsMapper = useMemo(() => projectGroupsElementPointsLinks
    .reduce((acc, link) => {
      const groupId = link.id_project_group;
      const subElementId = link.id_element_point;

      if (!acc[groupId]) acc[groupId] = new Set();
      acc[groupId].add(subElementId);

      return acc;
    }, {}), [projectGroupsElementPointsLinks]);

  const filterByGroup = (values, items, groupByIdsMapper) => {
    const group = values.projectGroup;
    const hasGroupFilter = group && group !== 0;
    const itemsByGroupId = groupByIdsMapper[group];

    return items.filter(e => (!hasGroupFilter ? true : itemsByGroupId?.has(e.id) ?? false));
  };

  const renderMenuItem = (key, value, text, active) => (
    {
      label: text,
      value,
      inactiveCondition: active !== undefined && !active,
      tooltip: active !== undefined && !active ? translate('common:Inactive') : '',
    }
  );

  const renderProjectGroups = () => [renderMenuItem('projectGroup_0', 0, translate('common:Select'))]
    .concat(...projectGroups.map(e => renderMenuItem(e.id, e.id, e.name, e.active)));

  const renderElements = values => [renderMenuItem('element_0', 0, translate('common:Select'))]
    .concat(
      filterByGroup(values, elements, groupToElementsMapper)
        .sort((a, b) => (a.name < b.name ? 1 : -1))
        .sort(a => a.active * -1)
        .map(el => renderMenuItem(el.id, el.id, el.name, el.active)),
    );

  const renderSubElements = values => [renderMenuItem('subElement_0', 0, translate('common:Select'))]
    .concat(
      filterByGroup(values, subElements, groupToSubElementsMapper)
        .filter(se => (!values.element || values.element === 0
          ? true
          : se.id_element === values.element))
        .sort((a, b) => (a.name < b.name ? 1 : -1))
        .sort(a => a.active * -1)
        .map(se => renderMenuItem(se.id, se.id, se.name, se.active)),
    );

  const renderDate = field => (
    <Field name={field} component={PF2MDatepicker} />
  );

  const renderSelect = (name, children, handleState) => (
    <>
      <Field
        name={name}
        render={({ field, form }) => (
          <PF2MSearchSelectOutlined
            placeholder={null}
            {...field}
            onChange={(e) => {
              form.setFieldValue(field.name, e.target.value, true);

              if (field.name === 'element') {
                form.setFieldValue('subElement', 0);
              }
              if (field.name === 'projectGroup') {
                form.setFieldValue('element', 0);
                form.setFieldValue('subElement', 0);
              }
              if (handleState) {
                handleState(e.target.value);
              }
            }}
            style={{ width: '100%' }}
          >
            {children}
          </PF2MSearchSelectOutlined>
        )}
      />
      <ErrorMessage
        name={name}
        render={msg => (
          <Typography
            color="error"
            variant="caption"
          >
            {msg}
          </Typography>
        )}
      />
    </>
  );

  const renderCheckbox = (name, label, checked) => (
    <Field
      name={name}
      render={({ field, form }) => (
        <FormControlLabel
          style={{ marginLeft: 0 }}
          control={(
            <Checkbox
              checked={!!checked}
              onChange={() => form.setFieldValue(field.name, !field.value)}
            />
          )}
          label={label}
        />
      )}
    />
  );

  return (
    <Formik
      isInitialValid
      initialValues={{
        startDate: filters.startDate || new Date(),
        endDate: filters.endDate || new Date(),
        projectGroup: filters.projectGroup ? filters.projectGroup.id : 0,
        element: filters.element ? filters.element.id : 0,
        subElement: filters.subElement ? filters.subElement.id : 0,
        hideInterruptedOrDeleted: filters.hideInterruptedOrDeleted,
      }}
      validate={(values) => {
        const {
          startDate,
          endDate,
        } = values;
        const startDateMoment = moment(startDate);
        const endDateMoment = moment(endDate);
        const errors = {};

        if (!startDateMoment.isValid()) {
          errors.startDate = translate('validation:InitialDateRequired');
        }

        if (!endDateMoment.isValid()) {
          errors.endDate = translate('validation:FinalDateRequired');
        }

        if (startDateMoment.isValid() && endDateMoment.isValid()) {
          const days = endDateMoment.diff(startDateMoment, 'days');
          if (days < 0) {
            errors.endDate = translate('validation:FinalDateSmallerThanInitialDate');
          }

          const searchLimit = process.env.REACT_APP_SEARCH_LIMIT_IN_DAYS || 7;
          if (days > searchLimit) {
            errors.endDate = `${translate('validation:SearchTimeRangeCantExceed')} ${searchLimit} ${translate('common:Days').toLowerCase()}`;
          }
        }

        return errors;
      }}
      onSubmit={async (values, { resetForm }) => {
        const {
          projectGroup: group,
          element: selectedElement,
          subElement,
          hideInterruptedOrDeleted,
        } = values;
        const currentProjectGroup = projectGroups.find(e => e.id === group);
        const currentElement = elements.find(e => e.id === selectedElement);
        const currentSubElment = subElements.find(e => e.id === subElement);

        dispatch(setUndergroundFilters({
          ...values,
          projectGroup: currentProjectGroup,
          element: currentElement,
          subElement: currentSubElment,
          startDate: parseDate(values.startDate, true),
          endDate: parseDate(values.endDate, true),
          hideInterruptedOrDeleted,
        }));
        dispatch(getOriginSubElements());
        resetForm();
        onSearch();
      }}
    >
      {({
        isSubmitting,
        isValid,
        values,
        resetForm,
      }) => (
        <Form>
          <Card style={{ width: 900 }}>
            <CardHeader icon>
              <>
                <CardIcon color="warning">
                  <SearchIcon />
                </CardIcon>
                <CardIcon
                  onClick={onClose}
                  style={{
                    float: 'right',
                    cursor: 'pointer',
                    background: 'darkgray',
                    borderRadius: 50,
                    boxShadow: '10px 10px 50px gray',
                  }}
                >
                  <CloseIcon />
                </CardIcon>
              </>
            </CardHeader>
            <CardBody>
              <Typography
                variant="h4"
                align="center"
                gutterBottom
              >
                {translate('common:Search').toUpperCase()}
              </Typography>
              <Grid container spacing={2} className={classes.fieldsContainer}>
                <Grid item container spacing={1} xs style={{ marginTop: 10 }}>
                  <Grid item xs>
                    <span className={classes.labelSpan}>
                      {translate('common:InitialDate')}
                    </span>
                    {renderDate('startDate')}
                  </Grid>
                  <Grid item xs>
                    <span className={classes.labelSpan}>
                      {translate('common:FinalDate')}
                    </span>
                    {renderDate('endDate')}
                  </Grid>
                  <Grid item xs />
                </Grid>
                <Grid item container spacing={1} xs style={{ marginTop: 10 }}>
                  <Grid item xs>
                    <span className={classes.labelSpan}>
                      {translate('common:ProjectGroup')}
                    </span>
                    {renderSelect('projectGroup', renderProjectGroups())}
                  </Grid>
                  <Grid item xs>
                    <span className={classes.labelSpan}>
                      {translate('common:Element')}
                    </span>
                    {renderSelect('element', renderElements(values))}
                  </Grid>
                  <Grid item xs>
                    <span className={classes.labelSpan}>
                      {translate('common:SubElement')}
                    </span>
                    {renderSelect('subElement', renderSubElements(values))}
                  </Grid>
                </Grid>
                <Grid item container spacing={1} xs style={{ marginTop: 10 }}>
                  <Grid item xs>
                    {renderCheckbox(
                      'hideInterruptedOrDeleted',
                      translate('common:HideInterruptedOrDeletedCycles'),
                      values.hideInterruptedOrDeleted,
                    )}
                  </Grid>
                </Grid>
                <Grid item style={{ alignSelf: 'center' }}>
                  <PF2MClearFiltersButton
                    onClick={() => resetForm({
                      startDate: newMomentDate(),
                      endDate: newMomentDate(),
                      projectGroup: 0,
                      element: 0,
                      subElement: 0,
                    })}
                  />
                  <PF2MSearchButton
                    type="submit"
                    disabled={isSubmitting || !isValid}
                    onClick={null}
                  />
                </Grid>
              </Grid>
            </CardBody>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

SearchForm.propTypes = {
  classes: PropTypes.object.isRequired,
  onSearch: PropTypes.func,
  onClose: PropTypes.func,
};

SearchForm.defaultProps = {
  onSearch: null,
  onClose: null,
};

export default withStyles(styles)(SearchForm);
